<div id="page-content" class="flex flex-col gap-8">
  <form [formGroup]="form" (keydown.enter)="$event.preventDefault()">
    <vs-form-field
      class="pb-10"
      [config]="{
        label: { text: 'Company logo', for: 'company-image-input' },
        description: 'Upload your company logo to be displayed at the top of the customer portal.',
      }"
    >
      <div class="flex flex-col gap-1" slot="before-content">
        <img class="w-24 h-24" [vsImagePreview]="form.controls.logo.value ?? logoUrl" />
        <!-- TODO: temporarily hiding as per Felix -->
        <!-- <button
            *ngIf="logoUrl || companyLogoInput.files[0]"
            vsLink
            (click)="companyLogoInput.clear(); resetLogo(); $event.stopImmediatePropagation()"
            class="text-xs"
          >
            Remove
          </button> -->
      </div>
      <vs-file-input
        formControlName="logo"
        [accept]="'.SVG, .PNG, .JPG, .JPEG, .GIF'"
        [moreInfo]="'max. 800x400px'"
        (change)="onImageUpload($event, 'logo')"
      ></vs-file-input>
    </vs-form-field>

    <vs-divider class="flex my-8 sm:hidden"></vs-divider>

    <vs-form-field
      class="pb-10"
      [config]="{
        label: { text: 'Avatar', for: 'avatar-logo-input' },
        description: 'Upload an icon-only (no text) version of your logo, to be displayed as the favicon in browsers.',
      }"
    >
      <div class="flex flex-col gap-1" slot="before-content">
        <img class="w-24 h-24" [vsImagePreview]="form.controls.avatar.value ?? avatarUrl" />
        <!-- TODO: temporarily hiding as per Felix -->
        <!-- <button
            vsLink
            (click)="avatarInput.clear(); resetAvatar(); $event.stopImmediatePropagation()"
            *ngIf="avatarUrl || avatarInput.files[0]"
            class="text-xs"
            [disabled]="disableAdminAvatar || staff.role!=='ADMIN'"
          >
            Remove
          </button> -->
      </div>
      <vs-file-input
        formControlName="avatar"
        [accept]="'.SVG, .PNG, .JPG, .JPEG, .GIF'"
        [moreInfo]="'max. 800x400px'"
        (change)="onImageUpload($event, 'avatar')"
      ></vs-file-input>
    </vs-form-field>

    <vs-divider class="flex my-8 sm:hidden"></vs-divider>

    <vs-form-field
      class="pb-10"
      [config]="{
        label: { text: 'Primary color', for: 'color-input' },
        description:
          'Use the picker to select the primary color for your portal. It will be used across the site as the default color for buttons, links, navigation, and more.',
      }"
    >
      <div slot="hint">
        <span *ngIf="getPreviewSeed(form.controls.prColor.value); else primaryColorError" class="text-gray-600">
          Your hue and saturation define a palette; shade 500 (50% lightness) is the theme color.
        </span>
        <ng-template #primaryColorError>
          <span class="text-danger">This color is not recognized.</span>
        </ng-template>
      </div>
      <div class="flex relative group w-fit h-fit" slot="before-content">
        <!-- Keep the picker value as the seed; the overlay displays the generated theme color. -->
        <input
          #primaryColorPicker
          vsInput
          [value]="form.controls.prColor.value || '#000000'"
          [disabled]="form.controls.prColor.disabled"
          aria-label="Primary color picker"
          size="lg"
          type="color"
        />
        <div class="pointer-events-none absolute inset-0 rounded-md overflow-hidden" aria-hidden="true">
          <div
            *ngIf="getPreviewSeed(form.controls.prColor.value) as seed; else primaryPreviewUnavailable"
            [vsCustomTheme]="seed"
            class="absolute inset-0 bg-themed-500"
          >
            <div
              class="flex opacity-0 transition-opacity absolute inset-0 border border-themed-300 group-hover:opacity-100 group-focus-within:opacity-100"
            >
              <div class="flex-1 h-full flex flex-col">
                <div class="flex-1 h-full bg-themed-50"></div>
                <div class="flex-1 h-full bg-themed-200"></div>
                <div class="flex-1 h-full bg-themed-400"></div>
                <div class="flex-1 h-full bg-themed-600"></div>
                <div class="flex-1 h-full bg-themed-800"></div>
              </div>
              <div class="flex-1 h-full flex flex-col">
                <div class="flex-1 h-full bg-themed-100"></div>
                <div class="flex-1 h-full bg-themed-300"></div>
                <div class="flex-1 h-full bg-themed-500"></div>
                <div class="flex-1 h-full bg-themed-700"></div>
                <div class="flex-1 h-full bg-themed-900"></div>
              </div>
            </div>
            <vs-icon name="palette" class="text-themed-100 absolute bottom-1 right-1"></vs-icon>
          </div>
          <ng-template #primaryPreviewUnavailable>
            <div
              class="flex items-center justify-center absolute inset-0 border border-gray-300 bg-gray-50 text-gray-500"
            >
              —
            </div>
          </ng-template>
        </div>
      </div>
      <vs-color-input #primaryColorInput formControlName="prColor" [colorPicker]="primaryColorPicker"></vs-color-input>
    </vs-form-field>

    <vs-divider class="flex my-8 sm:hidden"></vs-divider>

    <vs-form-field
      [config]="{
        label: { text: 'Accent color', for: 'color-input' },
        description:
          'Use the picker to select the accent color for your portal. It will be used across the site as the secondary color for buttons, links, navigation, and more.',
      }"
    >
      <div slot="hint">
        <span *ngIf="getPreviewSeed(form.controls.acColor.value); else accentColorError" class="text-gray-600">
          Your hue and saturation define a palette; shade 500 (50% lightness) is the theme color.
        </span>
        <ng-template #accentColorError>
          <span class="text-danger">This color is not recognized.</span>
        </ng-template>
      </div>
      <div class="flex relative group w-fit h-fit" slot="before-content">
        <!-- Keep the picker value as the seed; the overlay displays the generated theme color. -->
        <input
          #accentColorPicker
          vsInput
          [value]="form.controls.acColor.value || '#000000'"
          [disabled]="form.controls.acColor.disabled"
          aria-label="Accent color picker"
          size="lg"
          type="color"
        />
        <div class="pointer-events-none absolute inset-0 rounded-md overflow-hidden" aria-hidden="true">
          <div
            *ngIf="getPreviewSeed(form.controls.acColor.value) as seed; else accentPreviewUnavailable"
            [vsCustomTheme]="seed"
            class="absolute inset-0 bg-themed-500"
          >
            <div
              class="flex opacity-0 transition-opacity absolute inset-0 border border-themed-300 group-hover:opacity-100 group-focus-within:opacity-100"
            >
              <div class="flex-1 h-full flex flex-col">
                <div class="flex-1 h-full bg-themed-50"></div>
                <div class="flex-1 h-full bg-themed-200"></div>
                <div class="flex-1 h-full bg-themed-400"></div>
                <div class="flex-1 h-full bg-themed-600"></div>
                <div class="flex-1 h-full bg-themed-800"></div>
              </div>
              <div class="flex-1 h-full flex flex-col">
                <div class="flex-1 h-full bg-themed-100"></div>
                <div class="flex-1 h-full bg-themed-300"></div>
                <div class="flex-1 h-full bg-themed-500"></div>
                <div class="flex-1 h-full bg-themed-700"></div>
                <div class="flex-1 h-full bg-themed-900"></div>
              </div>
            </div>
            <vs-icon name="palette" class="text-themed-100 absolute bottom-1 right-1"></vs-icon>
          </div>
          <ng-template #accentPreviewUnavailable>
            <div
              class="flex items-center justify-center absolute inset-0 border border-gray-300 bg-gray-50 text-gray-500"
            >
              —
            </div>
          </ng-template>
        </div>
      </div>
      <vs-color-input #accentColorInput formControlName="acColor" [colorPicker]="accentColorPicker"></vs-color-input>
    </vs-form-field>

    <!-- Checkout Confirmation Text Section -->
    <div class="flex flex-col gap-2 mt-8 mb-4">
      <div class="grid grid-cols-1 sm:grid-cols-3 gap-4 items-start">
        <!-- Left column: label, description -->
        <div class="flex flex-col gap-2 sm:col-span-1">
          <label class="text-sm font-medium text-gray-700">Checkout Confirmation Text</label>
          <p class="text-xs text-gray-600 mb-1">
            This text will appear on the checkout confirmation page after a warranty purchase, between the completion
            message and warranty number.
          </p>
        </div>
        <!-- Right column: checkout confirmation text display/edit -->
        <div class="flex flex-col gap-2 sm:col-span-2">
          <textarea
            vsInput
            rows="3"
            class="cursor-text resize-none"
            [placeholder]="'Click to add checkout confirmation text...'"
            [value]="checkoutConfirmationTextPlain"
            (click)="openCheckoutConfirmationTextDialog()"
            (keydown)="onCheckoutConfirmationTextKeydown($event)"
          ></textarea>
        </div>
      </div>
    </div>
    <!-- End Checkout Confirmation Text Section -->

    <ws-edit-checkout-confirmation-text-dialog
      #checkoutConfirmationTextDialog
      [value]="form.value.checkoutConfirmationText"
      (saved)="onCheckoutConfirmationTextSaved($event)"
    ></ws-edit-checkout-confirmation-text-dialog>

    <div
      class="flex flex-col-reverse sm:flex-row sm:justify-end items-center gap-3 pb-4"
      *ngIf="form.dirty && canEditOrganization"
    >
      <button
        vs-button
        class="sm:flex-none w-full sm:w-fit"
        appearance="outline"
        [disabled]="!form.dirty"
        (click)="resetForm()"
      >
        Cancel
      </button>
      <button
        vs-button
        class="sm:flex-none w-full sm:w-fit"
        theme="primary"
        [disabled]="!inputIsValid() || !form.dirty"
        (click)="confirm()"
      >
        Save
      </button>
    </div>
  </form>
</div>
